import { createApi } from '@reduxjs/toolkit/query/react';
import axiosBaseQuery from './queries';
import { ProjectTask, CreateProjectTaskRequest, UpdateProjectTaskRequest } from 'types';

// Response types
interface ProjectTasksResponse {
  success: boolean;
  projectTasks: ProjectTask[];
}

interface ProjectTaskResponse {
  success: boolean;
  projectTask: ProjectTask;
  nextTask?: ProjectTask;  // Returned when completing a repeatable task
}

interface ApplyTemplateResponse {
  success: boolean;
  message: string;
  projectTasks: ProjectTask[];
}

// Request types
interface CompleteTaskRequest {
  status: 'pending' | 'completed';
}

interface ApplyTemplateRequest {
  templateId?: string;
  template_id?: string;
  taskIds?: string[];
  task_ids?: string[];
  baseDate?: string;
  base_date?: string;
  assigneeIds?: string[];
  assignee_ids?: string[];
}

// Stats types
interface ProjectTaskStats {
  total: number;
  pending: number;
  overdue: number;
  completed: number;
}

interface ProjectTaskStatsResponse {
  success: boolean;
  stats: ProjectTaskStats;
}

export const taskApi = createApi({
  reducerPath: 'taskApi',
  baseQuery: axiosBaseQuery({
    baseUrl: import.meta.env.VITE_APP_BASE_URL as string,
  }),
  tagTypes: ['ProjectTask'],
  endpoints: (builder) => ({
    // ============================================
    // ProjectTask (nested under workspace/project)
    // ============================================
    
    // Get all project tasks
    getProjectTasks: builder.query<ProjectTask[], { 
      workspaceId: string; 
      projectId: string; 
      status?: string;
      isActive?: boolean;
      hasDueDate?: boolean;
      fromDate?: string;  // ISO date string (YYYY-MM-DD)
      toDate?: string;    // ISO date string (YYYY-MM-DD)
      assigneeIds?: string[];  // Array of user IDs
    }>({
      query: ({ workspaceId, projectId, status, isActive, hasDueDate, fromDate, toDate, assigneeIds }) => {
        const params = new URLSearchParams();
        if (status) params.append('status', status);
        if (isActive !== undefined) params.append('is_active', String(isActive));
        if (hasDueDate !== undefined) params.append('has_due_date', String(hasDueDate));
        if (fromDate) params.append('from_date', fromDate);
        if (toDate) params.append('to_date', toDate);
        if (assigneeIds && assigneeIds.length > 0) params.append('assignee_ids', assigneeIds.join(','));
        
        return { 
          url: `/v1/workspaces/${workspaceId}/projects/${projectId}/tasks/${params.toString() ? '?' + params.toString() : ''}`, 
          method: 'get'
        };
      },
      transformResponse: (response: ProjectTasksResponse) => response.projectTasks,
      providesTags: ['ProjectTask'],
    }),

    getWorkspaceProjectTasks: builder.query<ProjectTask[], { 
      workspaceId: string; 
      status?: string;
      isActive?: boolean;
      hasDueDate?: boolean;
      fromDate?: string;
      toDate?: string;
      assigneeIds?: string[];
    }>({
      query: ({ workspaceId, status, isActive, hasDueDate, fromDate, toDate, assigneeIds }) => {
        const params = new URLSearchParams();
        if (status) params.append('status', status);
        if (isActive !== undefined) params.append('is_active', String(isActive));
        if (hasDueDate !== undefined) params.append('has_due_date', String(hasDueDate));
        if (fromDate) params.append('from_date', fromDate);
        if (toDate) params.append('to_date', toDate);
        if (assigneeIds && assigneeIds.length > 0) params.append('assignee_ids', assigneeIds.join(','));
        
        return {
          url: `/v1/workspaces/${workspaceId}/tasks/project-tasks/${params.toString() ? '?' + params.toString() : ''}`,
          method: 'get',
        };
      },
      transformResponse: (response: ProjectTasksResponse) => response.projectTasks,
      providesTags: ['ProjectTask'],
    }),
    
    // Get project task statistics
    getProjectTaskStats: builder.query<ProjectTaskStats, { workspaceId: string; projectId: string }>({
      query: ({ workspaceId, projectId }) => ({ 
        url: `/v1/workspaces/${workspaceId}/projects/${projectId}/tasks/stats/`, 
        method: 'get'
      }),
      transformResponse: (response: ProjectTaskStatsResponse) => response.stats,
      providesTags: ['ProjectTask'],
    }),
    
    // Get single project task
    getProjectTask: builder.query<ProjectTask, { workspaceId: string; projectId: string; taskId: string }>({
      query: ({ workspaceId, projectId, taskId }) => ({ 
        url: `/v1/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}/`, 
        method: 'get' 
      }),
      transformResponse: (response: ProjectTaskResponse) => response.projectTask,
      providesTags: ['ProjectTask'],
    }),
    
    // Create project task
    createProjectTask: builder.mutation<ProjectTask, { workspaceId: string; projectId: string; data: CreateProjectTaskRequest }>({
      query: ({ workspaceId, projectId, data }) => ({ 
        url: `/v1/workspaces/${workspaceId}/projects/${projectId}/tasks/`, 
        method: 'post', 
        data 
      }),
      transformResponse: (response: ProjectTaskResponse) => response.projectTask,
      invalidatesTags: ['ProjectTask'],
    }),
    
    // Update project task
    updateProjectTask: builder.mutation<ProjectTask, { workspaceId: string; projectId: string; taskId: string; data: UpdateProjectTaskRequest }>({
      query: ({ workspaceId, projectId, taskId, data }) => ({ 
        url: `/v1/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}/`, 
        method: 'patch', 
        data 
      }),
      transformResponse: (response: ProjectTaskResponse) => response.projectTask,
      invalidatesTags: ['ProjectTask'],
    }),
    
    // Delete project task
    deleteProjectTask: builder.mutation<void, { workspaceId: string; projectId: string; taskId: string }>({
      query: ({ workspaceId, projectId, taskId }) => ({ 
        url: `/v1/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}/`, 
        method: 'delete' 
      }),
      invalidatesTags: ['ProjectTask'],
    }),
    
    // Complete/Uncomplete project task
    completeProjectTask: builder.mutation<{ projectTask: ProjectTask; nextTask?: ProjectTask }, { 
      workspaceId: string; 
      projectId: string; 
      taskId: string; 
      status: 'pending' | 'completed' 
    }>({
      query: ({ workspaceId, projectId, taskId, status }) => ({
        url: `/v1/workspaces/${workspaceId}/projects/${projectId}/tasks/${taskId}/complete/`,
        method: 'post',
        data: { status },
      }),
      transformResponse: (response: ProjectTaskResponse) => ({
        projectTask: response.projectTask,
        nextTask: response.nextTask,
      }),
      invalidatesTags: ['ProjectTask'],
    }),
    
    // Apply template to project (creates ProjectTasks from template)
    applyTemplateToProject: builder.mutation<ProjectTask[], { 
      workspaceId: string; 
      projectId: string; 
      data: ApplyTemplateRequest 
    }>({
      query: ({ workspaceId, projectId, data }) => ({
        url: `/v1/workspaces/${workspaceId}/projects/${projectId}/tasks/apply-template/`,
        method: 'post',
        data,
      }),
      transformResponse: (response: ApplyTemplateResponse) => response.projectTasks,
      invalidatesTags: ['ProjectTask'],
    }),
  }),
});

export const {
  // ProjectTask hooks
  useGetProjectTasksQuery,
  useGetWorkspaceProjectTasksQuery,
  useGetProjectTaskStatsQuery,
  useGetProjectTaskQuery,
  useCreateProjectTaskMutation,
  useUpdateProjectTaskMutation,
  useDeleteProjectTaskMutation,
  useCompleteProjectTaskMutation,
  useApplyTemplateToProjectMutation,
} = taskApi;

// Legacy aliases for backward compatibility
export const useGetRoutineTasksQuery = useGetProjectTasksQuery;
export const useGetTaskInstancesQuery = useGetProjectTasksQuery;
export const useCreateRoutineTaskMutation = useCreateProjectTaskMutation;
export const useCompleteTaskInstanceMutation = useCompleteProjectTaskMutation;
